import React, { useState } from 'react';

/**
 * MiloOriginBadge — the ONE shared "this came from Milo" indicator, used platform-
 * wide so the marker is identical everywhere a Milo-created/approved record lands
 * (Calendar, Event Readiness, Finance, Guests, Vendors, Program, Messaging) and on
 * Notebook items themselves.
 *
 * Deliberately dependency-light (no `motion`, no icon lib) so any module can adopt
 * it without pulling extra bundle weight. Subtle by design — a small gold sparkle
 * chip; hover/focus reveals provenance detail. If a human later edits the record,
 * pass `modified` so BOTH facts show ("by Milo · edited by you").
 *
 * Props:
 *  createdBy      — 'milo' | 'human' (renders nothing for pure-human unless `always`)
 *  approvedAt     — ISO string (optional)
 *  approvedBy     — 'human' | undefined
 *  sourceLabel    — e.g. "Notebook entry" (what it was created from)
 *  sourceSnippet  — the originating text (shown in the tooltip)
 *  modified       — boolean: a human edited it after creation
 *  onOpenSource   — () => void : jump to the source (makes the chip a button)
 *  size           — 'sm' | 'xs'
 */
const GOLD = '#C6A75A';
const GOLD_DEEP = '#8A7847';

function fmt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return null; }
}

export function MiloOriginBadge({
  createdBy = 'milo', approvedAt, approvedBy, sourceLabel = 'Notebook entry',
  sourceSnippet, modified = false, modifiedAt, confidence, onOpenSource,
  size = 'sm', always = false,
}) {
  const [open, setOpen] = useState(false);
  if (createdBy !== 'milo' && !always) return null;

  const dims = size === 'xs'
    ? { font: 9.5, pad: '1px 6px', gap: 3, dot: 5 }
    : { font: 10.5, pad: '2px 8px', gap: 4, dot: 6 };

  const approvedOn = fmt(approvedAt);
  const modOn = fmt(modifiedAt);
  const pct = (typeof confidence === 'number' && confidence >= 0 && confidence <= 1)
    ? `${Math.round(confidence * 100)}%` : null;
  const detailLines = [
    createdBy === 'milo' ? 'Created by Milo' : 'Created by you',
    sourceLabel ? `Source: ${sourceLabel}` : null,
    sourceSnippet ? `“${String(sourceSnippet).slice(0, 80)}${sourceSnippet.length > 80 ? '…' : ''}”` : null,
    approvedOn ? `Approved ${approvedBy === 'human' ? 'by you ' : ''}on ${approvedOn}` : null,
    pct ? `Confidence: ${pct}` : null,
    modified ? `Subsequently edited by you${modOn ? ` on ${modOn}` : ''}` : null,
  ].filter(Boolean);

  const Tag = onOpenSource ? 'button' : 'span';

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <Tag
        type={onOpenSource ? 'button' : undefined}
        onClick={onOpenSource}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={detailLines.join('. ')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: dims.gap,
          padding: dims.pad, borderRadius: 999, cursor: onOpenSource ? 'pointer' : 'default',
          background: 'linear-gradient(135deg, rgba(198,167,90,0.16), rgba(198,167,90,0.07))',
          border: '1px solid rgba(198,167,90,0.4)',
          color: GOLD_DEEP, fontSize: dims.font, fontWeight: 600, letterSpacing: '0.02em',
          lineHeight: 1.4, whiteSpace: 'nowrap',
        }}
      >
        {/* tiny gold "sparkle" mark, pure CSS */}
        <span aria-hidden style={{
          width: dims.dot, height: dims.dot, borderRadius: 999, flexShrink: 0,
          background: `radial-gradient(circle at 32% 28%, #f3dca8 0%, ${GOLD} 60%, ${GOLD_DEEP} 100%)`,
          boxShadow: '0 0 0 1px rgba(255,255,255,0.4) inset',
        }} />
        Milo{modified ? ' · edited' : ''}
      </Tag>

      {open && detailLines.length > 0 && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, zIndex: 60,
            minWidth: 180, maxWidth: 260, padding: '8px 10px', borderRadius: 10,
            background: 'rgba(20,25,40,0.94)', color: '#fdf8ec', fontSize: 11, lineHeight: 1.5,
            border: '1px solid rgba(198,167,90,0.35)', boxShadow: '0 10px 30px -8px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)', textAlign: 'left', whiteSpace: 'normal', pointerEvents: 'none',
          }}
        >
          {detailLines.map((line, i) => (
            <div key={i} style={{ color: i === 0 ? GOLD : 'rgba(253,248,236,0.85)', fontWeight: i === 0 ? 600 : 400 }}>
              {line}
            </div>
          ))}
          {onOpenSource && (
            <div style={{ marginTop: 4, color: GOLD, fontStyle: 'italic' }}>Click to view source →</div>
          )}
        </span>
      )}
    </span>
  );
}

export default MiloOriginBadge;
